import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Copy, Check, MessageCircle, Sparkles, Calendar, ArrowUpRight, Printer,
} from "lucide-react";
import { api } from "@/lib/api";
import { useContent } from "@/lib/ContentProvider";

/**
 * GiftVoucher V2 — expérience digitale du bon cadeau.
 *
 * Objectif : maximum 3 écrans, entièrement pensée mobile-first, sensation
 * premium. Aucun scroll dans un écran donné : chaque section fait exactement
 * la hauteur du viewport (100svh) et l'utilisateur défile écran par écran.
 *
 *   Écran 1 · Intro + enveloppe scellée (touch to open)
 *   Écran 2 · Révélation : porte du refuge (vidéo boucle) + nom + phrase
 *              personnalisée + résumé du cadeau
 *   Écran 3 · Code cadeau + 4 boutons (Copier · Dates · Question · Imprimer)
 *
 * Route : /bon-cadeau/:code
 */

const LODGIFY_URL = "https://curieuxrefuge.lodgify.com/fr/disponibilites";
const DOOR_VIDEO_MP4 = "/media/videos/entree-porte.mp4";
const DOOR_VIDEO_WEBM = "/media/videos/entree-porte.webm";
const DOOR_POSTER = "/media/images/entree-porte-poster.jpg";

// ============================================================
// Occasion → phrase française naturelle
// ============================================================
function occasionPhrase(raw) {
  const k = (raw || "").toLowerCase().trim();
  if (!k) return "Simplement pour vous faire plaisir.";
  if (/anniv/.test(k))       return "Pour célébrer votre anniversaire.";
  if (/mariage|noce/.test(k))return "Pour votre mariage.";
  if (/lune de miel|honeymoon/.test(k)) return "Pour votre lune de miel.";
  if (/valentin/.test(k))    return "Pour votre Saint-Valentin.";
  if (/noel|noël|nowel/.test(k)) return "Pour Noël.";
  if (/nouvel an|new year|reveillon|réveillon/.test(k)) return "Pour le Nouvel An.";
  if (/merci|remerc/.test(k))return "Pour vous remercier.";
  if (/felic|félic/.test(k)) return "Pour vous féliciter.";
  if (/depart|départ|retraite/.test(k)) return "Pour marquer ce nouveau départ.";
  if (/naissance/.test(k))   return "Pour célébrer cette naissance.";
  if (/surprise/.test(k))    return "Simplement pour vous surprendre.";
  return `Pour ${raw.trim().toLowerCase()}.`;
}

export default function GiftVoucher() {
  const { code } = useParams();
  const { contact, content, mediaUrl } = useContent();
  const [card, setCard] = useState(null);
  const [err, setErr] = useState("");
  const [opened, setOpened] = useState(false);
  const [copied, setCopied] = useState(false);
  const reveal2Ref = useRef(null);

  useEffect(() => {
    let alive = true;
    api.getGiftCard(code)
      .then((c) => { if (alive) setCard(c); })
      .catch((e) => { if (alive) setErr(e?.response?.data?.detail || "Bon introuvable"); });
    return () => { alive = false; };
  }, [code]);

  useEffect(() => {
    document.title = card
      ? `Cadeau pour ${card.beneficiary?.firstName || ""} — Curieux Refuge`
      : "Bon cadeau — Curieux Refuge";
  }, [card]);

  useEffect(() => {
    // Après l'ouverture de l'enveloppe → défilement doux vers l'écran 2
    if (opened && reveal2Ref.current) {
      setTimeout(() => {
        reveal2Ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 750);
    }
  }, [opened]);

  const waNumber = (contact?.whatsappNumber || "33692793155").replace(/\D/g, "");
  const waHref = `https://wa.me/${waNumber}?text=${encodeURIComponent("Une question sur votre cadeau ?")}`;

  const copyCode = async () => {
    if (!card?.code) return;
    try { await navigator.clipboard.writeText(card.code); }
    catch (_e) {
      const input = document.createElement("input");
      input.value = card.code;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const phrase = useMemo(() => occasionPhrase(card?.occasion), [card]);

  if (err) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-6 text-center"
        style={{ background: "linear-gradient(180deg, #FBF5EA 0%, #FDF9F0 100%)" }}
      >
        <div>
          <Sparkles size={22} className="mx-auto mb-3" style={{ color: "var(--cr-ruby)" }} strokeWidth={1.2} />
          <p className="font-display italic text-lg" style={{ color: "var(--cr-ink)" }}>{err}</p>
        </div>
      </div>
    );
  }
  if (!card) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#FDF9F0" }}>
        <div className="w-8 h-8 rounded-full border-2 border-refuge-ruby/30 border-t-refuge-ruby animate-spin" />
      </div>
    );
  }

  const beneficiary = card.beneficiary?.firstName || "";
  const offerant = card.offerant?.firstName || "";
  const options = card.options || {};
  const prestations = [];
  if (options.petitDej) prestations.push("Petit-déjeuner inclus");
  if (options.bain) prestations.push("Bain de fleurs inclus");
  if (options.deco) prestations.push("Décor du Refuge inclus");

  const validUntilFmt = card.validUntil
    ? new Date(card.validUntil).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })
    : "—";

  // Médias configurables via l'admin (config globale content.giftMedia)
  const cfg = content?.giftMedia || {};
  const resolveMedia = (name, fallback) => {
    if (!name) return fallback;
    // Chemin statique déjà complet → laisse tel quel
    if (name.startsWith("/") || name.startsWith("http")) return name;
    // Nom de fichier issu de la bibliothèque → passe par mediaUrl (cache-busting)
    return mediaUrl ? mediaUrl(`/${name}`) : `${process.env.REACT_APP_BACKEND_URL}/api/media/${encodeURIComponent(name)}`;
  };
  const doorVideoSrc = resolveMedia(cfg.video, DOOR_VIDEO_MP4);
  const doorPosterSrc = resolveMedia(cfg.poster, DOOR_POSTER);
  const isVideoFile = /\.(mp4|webm|mov|ogg|ogv|m4v)$/i.test(doorVideoSrc);

  return (
    <div
      data-testid="gift-voucher"
      className="relative overflow-x-hidden"
      style={{
        background: "linear-gradient(180deg, #FBF5EA 0%, #FDF9F0 40%, #FFFFFF 100%)",
        color: "var(--cr-ink)",
      }}
    >
      {/* Grain papier discret */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 opacity-[0.04] z-0"
        style={{
          background: "repeating-linear-gradient(0deg, rgba(46,31,24,0.35) 0px, rgba(46,31,24,0) 1px, rgba(46,31,24,0) 3px)",
        }}
      />

      {/* ===== ÉCRAN 1 · Intro + enveloppe (sans scroll interne) ===== */}
      <section
        data-testid="voucher-screen-1"
        className="relative z-10 flex flex-col items-center justify-between text-center px-6"
        style={{ minHeight: "100svh", paddingTop: "6svh", paddingBottom: "6svh" }}
      >
        <div>
          <p
            className="font-body tracking-[0.42em] uppercase opacity-70"
            style={{ color: "var(--cr-ruby)", fontSize: "10px" }}
          >
            Curieux Refuge
          </p>
          <p
            className="font-display italic font-light mt-4 leading-[1.15]"
            style={{
              color: "var(--cr-ink)",
              fontSize: "clamp(1.55rem, 6vw, 2.1rem)",
            }}
          >
            Quelqu'un a pensé à vous…
          </p>
          <div className="flex justify-center mt-4">
            <span className="h-[1px] w-12" style={{ background: "var(--cr-gold)", opacity: 0.65 }} />
          </div>
        </div>

        <button
          type="button"
          onClick={() => setOpened(true)}
          data-testid="envelope-open"
          aria-label="Ouvrir l'enveloppe"
          className="group relative focus:outline-none active:scale-95 transition-transform"
        >
          <Envelope opened={opened} />
        </button>

        <p
          className="font-display italic opacity-70"
          style={{ color: "var(--cr-ink-soft)", fontSize: "0.9rem" }}
        >
          Touchez l'enveloppe pour l'ouvrir.
        </p>
      </section>

      {/* ===== ÉCRAN 2 · Révélation avec vidéo porte ===== */}
      {opened && (
        <section
          ref={reveal2Ref}
          data-testid="voucher-screen-2"
          className="relative z-10 flex flex-col items-center justify-center px-5 cr-fade-up"
          style={{ minHeight: "100svh", paddingTop: "5svh", paddingBottom: "5svh" }}
        >
          <div className="max-w-md w-full text-center">
            {/* Vidéo porte du refuge (identité) */}
            <div
              className="mx-auto mb-6 overflow-hidden"
              style={{
                aspectRatio: "4 / 5",
                maxWidth: "min(320px, 78vw)",
                borderRadius: "14px",
                border: "1px solid rgba(184, 153, 104, 0.42)",
                boxShadow: "0 20px 55px -30px rgba(46,31,24,0.55)",
                background: "#0d0805",
              }}
            >
              <video
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                poster={doorPosterSrc}
                className="w-full h-full object-cover"
                key={doorVideoSrc}
              >
                {isVideoFile && <source src={doorVideoSrc} />}
                {/* Fallback WebM du refuge (uniquement si aucune vidéo custom) */}
                {!cfg.video && <source src={DOOR_VIDEO_WEBM} type="video/webm" />}
              </video>
            </div>

            {/* Nom du bénéficiaire */}
            <p
              className="font-display font-light leading-[1.05] mb-2"
              style={{
                color: "var(--cr-ink)",
                fontSize: "clamp(1.9rem, 8vw, 2.6rem)",
              }}
            >
              {beneficiary || "Vous"},
            </p>

            {/* Phrase du cadeau — V2 « un moment » plutôt que « une parenthèse » */}
            <p
              className="font-display italic font-light leading-[1.35] px-2"
              style={{
                color: "var(--cr-ruby)",
                fontSize: "clamp(1.05rem, 4.2vw, 1.25rem)",
              }}
            >
              {offerant ? `${offerant} vous offre` : "on vous offre"} un moment au Curieux Refuge.
            </p>

            {/* Phrase occasion dynamique */}
            <p
              className="font-display italic opacity-80 mt-3"
              style={{
                color: "var(--cr-ink-soft)",
                fontSize: "clamp(0.95rem, 3.6vw, 1.05rem)",
              }}
            >
              {phrase}
            </p>

            {/* Résumé du cadeau — compact */}
            <div
              className="mt-6 mx-auto rounded-xl px-5 py-4"
              style={{
                background: "rgba(255,253,249,0.85)",
                border: "1px solid rgba(184, 153, 104, 0.35)",
                maxWidth: "22rem",
                boxShadow: "0 12px 34px -30px rgba(139, 37, 48, 0.4)",
              }}
            >
              <p
                className="font-display font-light leading-tight"
                style={{ color: "var(--cr-ink)", fontSize: "1.15rem" }}
              >
                {card.nights} {card.nights > 1 ? "nuits" : "nuit"} pour {card.travelers} {card.travelers > 1 ? "personnes" : "personne"}
              </p>
              <p
                className="font-display italic font-light mt-0.5"
                style={{ color: "var(--cr-ink-soft)", fontSize: "0.9rem" }}
              >
                Formule {card.formule === "weekend" ? "Week-end" : "Semaine"}
              </p>
              {prestations.length > 0 && (
                <ul className="mt-3 space-y-0.5">
                  {prestations.map((p) => (
                    <li
                      key={p}
                      className="font-display font-light"
                      style={{ color: "var(--cr-ink)", fontSize: "0.92rem" }}
                    >
                      · {p}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ===== ÉCRAN 3 · Code + 4 boutons + signature ===== */}
      {opened && (
        <section
          data-testid="voucher-screen-3"
          className="relative z-10 flex flex-col items-center justify-center px-6 text-center cr-fade-up"
          style={{ minHeight: "100svh", paddingTop: "5svh", paddingBottom: "5svh" }}
        >
          <div className="max-w-md w-full">
            <p
              className="font-body tracking-[0.32em] uppercase mb-4"
              style={{ color: "var(--cr-ruby)", fontSize: "10px" }}
            >
              Votre code cadeau
            </p>

            <div
              className="rounded-xl px-5 py-4 mb-4"
              style={{
                background: "#FFFDF9",
                border: "1.5px dashed rgba(139, 37, 48, 0.55)",
              }}
            >
              <p
                data-testid="gift-code"
                className="font-mono tracking-[0.14em] select-all"
                style={{
                  color: "var(--cr-ink)",
                  fontSize: "clamp(1.35rem, 6vw, 1.8rem)",
                  fontWeight: 500,
                }}
              >
                {card.code}
              </p>
            </div>

            <p
              className="font-body italic mb-6"
              style={{ color: "var(--cr-ink-soft)", fontSize: "0.78rem" }}
            >
              Valable jusqu'au {validUntilFmt}<br />
              Sous réserve de disponibilité aux dates choisies
            </p>

            {/* 4 boutons */}
            <div className="space-y-2.5">
              <button
                type="button"
                onClick={copyCode}
                data-testid="copy-code-btn"
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full transition-all font-body text-[11px] tracking-[0.22em] uppercase"
                style={{
                  border: `1px solid ${copied ? "var(--cr-ruby)" : "rgba(139, 37, 48, 0.45)"}`,
                  background: copied ? "var(--cr-ruby)" : "transparent",
                  color: copied ? "#FAF4EC" : "var(--cr-ruby)",
                }}
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? "Code copié" : "Copier le code"}
              </button>

              <a
                href={LODGIFY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-dates"
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full transition-all font-body text-sm tracking-wide"
                style={{
                  background: "var(--cr-ruby)",
                  color: "#FAF4EC",
                }}
              >
                <Calendar size={15} strokeWidth={1.6} />
                Choisir mes dates
                <ArrowUpRight size={14} strokeWidth={1.4} />
              </a>

              <a
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-whatsapp"
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full transition-all font-body text-[11px] tracking-[0.22em] uppercase"
                style={{
                  border: "1px solid rgba(139, 37, 48, 0.35)",
                  color: "var(--cr-ruby)",
                }}
              >
                <MessageCircle size={13} strokeWidth={1.4} />
                Une question&nbsp;?
              </a>

              <a
                href={`/bon-cadeau/${card.code}/print`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-print"
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full transition-all font-body text-[11px] tracking-[0.22em] uppercase"
                style={{
                  border: "1px solid rgba(184, 153, 104, 0.55)",
                  color: "var(--cr-ink-soft)",
                }}
              >
                <Printer size={13} strokeWidth={1.4} />
                Télécharger / Imprimer le bon
              </a>
            </div>

            {/* Signature */}
            <div className="mt-8">
              <div className="flex justify-center mb-3">
                <span className="h-[1px] w-14" style={{ background: "var(--cr-gold)", opacity: 0.65 }} />
              </div>
              <p
                className="font-display italic font-light"
                style={{ color: "var(--cr-ruby)", fontSize: "0.95rem" }}
              >
                Que la magie opère.
              </p>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

// ============================================================
// Enveloppe — état fermé/ouvert (subtle SVG)
// ============================================================
function Envelope({ opened }) {
  const w = "clamp(220px, 72vw, 300px)";
  const h = "clamp(150px, 48vw, 210px)";
  return (
    <div className="relative transition-transform duration-500" style={{ width: w, height: h }}>
      {/* Corps */}
      <div
        className="absolute inset-0 rounded-sm"
        style={{
          background: "linear-gradient(180deg, #FFFDF7 0%, #F5EAD4 100%)",
          border: "1px solid rgba(139, 37, 48, 0.25)",
          boxShadow: "0 20px 55px -25px rgba(46,31,24,0.35), 0 4px 12px -4px rgba(46,31,24,0.15)",
        }}
      />
      {/* Rabat triangulaire (rotate à l'ouverture) */}
      <svg
        viewBox="0 0 300 200"
        className="absolute inset-0 w-full h-full transition-transform duration-700"
        style={{
          overflow: "visible",
          transformOrigin: "50% 0%",
          transform: opened ? "rotateX(180deg) translateY(-6px)" : "rotateX(0deg)",
        }}
      >
        <path
          d="M 0 0 L 150 110 L 300 0 Z"
          fill="url(#flap)"
          stroke="rgba(139, 37, 48, 0.28)"
          strokeWidth="1"
        />
        <defs>
          <linearGradient id="flap" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FFFAF0" />
            <stop offset="100%" stopColor="#EDDEC1" />
          </linearGradient>
        </defs>
      </svg>
      {/* Sceau ruby (disparaît à l'ouverture) */}
      <div
        className="absolute left-1/2 top-1/2 rounded-full flex items-center justify-center transition-all duration-500"
        style={{
          width: "clamp(50px, 15vw, 62px)",
          height: "clamp(50px, 15vw, 62px)",
          transform: `translate(-50%, -50%) scale(${opened ? 0.5 : 1})`,
          opacity: opened ? 0 : 1,
          background: "radial-gradient(circle at 30% 30%, #A83646 0%, #8B2530 55%, #6A1C25 100%)",
          boxShadow: "inset 0 -3px 6px rgba(0,0,0,0.35), 0 3px 8px rgba(139,37,48,0.5)",
          color: "#F5DFB5",
          animation: opened ? "none" : "cr-seal-pulse 3.2s ease-in-out infinite",
        }}
      >
        <span className="font-display italic" style={{ fontSize: "clamp(1rem, 3.4vw, 1.3rem)" }}>
          CR
        </span>
      </div>
    </div>
  );
}
